
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoadingState } from '@/components/error-handling/LoadingState';
import { Job } from '@/types/job'; // Use the created Job type
import { useAvailableJobs } from './useAvailableJobs'; // Assuming this hook provides unlinked jobs

const formSchema = z.object({
  jobId: z.string().min(1, 'Please select a job'),
});

interface LinkJobFormProps {
  clientId: string;
  onSubmit: (jobId: string) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  // availableJobs: Job[]; // Props to receive available jobs
  // isLoadingJobs: boolean; // Prop for loading state of available jobs
}

const LinkJobForm: React.FC<LinkJobFormProps> = ({
  clientId,
  onSubmit,
  onCancel,
  isSubmitting,
  // availableJobs, // Use prop
  // isLoadingJobs, // Use prop
}) => {
  const { data: availableJobs = [], isLoading: isLoadingJobs, error } = useAvailableJobs(clientId); // Fetch jobs here

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      jobId: '',
    },
  });

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    onSubmit(values.jobId);
  };

  if (isLoadingJobs) {
    return <LoadingState message="Loading available jobs..." />;
  }
  if (error) {
    return <p className="text-red-500">Error loading jobs: {error.message}</p>;
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="jobId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Select Job to Link</FormLabel>
              <Select
                onValueChange={field.onChange}
                defaultValue={field.value}
                disabled={isSubmitting || isLoadingJobs || availableJobs.length === 0}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={availableJobs.length === 0 ? "No available jobs to link" : "Select a job"} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {availableJobs.map((job) => (
                    <SelectItem key={job.id} value={job.id}>
                      {job.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || isLoadingJobs || availableJobs.length === 0}>
            {isSubmitting ? 'Linking...' : 'Link Job'}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default LinkJobForm;
